import { Col, Row, Alert } from 'antd';
import { AlertProps } from 'antd/lib/alert';
import chunk from 'lodash/chunk';
import min from 'lodash/min';
import React from 'react';

import { IWidgetBlockComponent } from '../types/IWidgetBlock';
import { WarningBoxModel, WarningBoxModelImmutable } from './types';
import { Container, WarningBoxAlertWrapper, WarningBoxSummaryWrapper, WarningCardContainer } from './WarningBox.style';
import WarningCard from './WarningCard';

const DEFAULT_WARNING_SAFE_COLOUR = '#00A854';
const DEFAULT_WARNING_COLOUR = '#FE9200';
const DEFAULT_WARNING_UNKNOWN_COLOUR = '#008EFB';
const SAFE = 'safe';
const WARN = 'warn';
const UNCLEAR = 'unclear';

const WarningBox: React.FC<IWidgetBlockComponent<WarningBoxModel | WarningBoxModelImmutable>> = ({ widgetModel }) => {
    if (!widgetModel) {
        return null;
    }

    const model = (widgetModel as WarningBoxModelImmutable).toJS?.() || widgetModel;
    const { blockBackgroundColor, summariseWarnings, datums: warnings } = model;
    const fontSize = model.fontSize || 100;
    const rowSize = min([warnings.length, model.rowSize]) || 1;

    const numberOfAlerts = warnings.reduce((acc, { threshold }) => acc + (threshold ? 1 : 0), 0);
    const unknownWarnings = warnings.reduce((acc, { unclear }) => acc + (unclear ? 1 : 0), 0);
    const status = numberOfAlerts > 0 ? WARN : unknownWarnings === 0 ? SAFE : UNCLEAR;

    const statusViews = {
        warn: {
            message: () => 'Warning',
            description: (numberOfAlerts: number) =>
                numberOfAlerts == 1 ? `There is ${numberOfAlerts} active warning` : `There are ${numberOfAlerts} active warnings`,
            type: 'warning',
            color: DEFAULT_WARNING_COLOUR, // an alert but should be the defined color
        },
        safe: {
            message: () => 'No warning conditions',
            description: () => '',
            type: 'success',
            color: DEFAULT_WARNING_SAFE_COLOUR,
        },
        unclear: {
            message: () => 'Some conditions are unknown',
            description: () => `The status of ${unknownWarnings} ${unknownWarnings === 1 ? 'warning is' : 'warnings are'} unknown`,
            type: 'info',
            color: DEFAULT_WARNING_UNKNOWN_COLOUR,
        },
    };

    const summary = summariseWarnings
        ? {
              message: statusViews[status].message(),
              description: statusViews[status].description(numberOfAlerts),
              type: statusViews[status].type,
          }
        : null;

    const rows = chunk(warnings, rowSize) || [];
    const borderColor = statusViews[status].color;
    const summaryHeight = summariseWarnings ? (numberOfAlerts > 0 || unknownWarnings ? 68 : 45) : 0;

    return (
        <Container className='warningBoxRender' borderColor={borderColor}>
            {summary && (
                <WarningBoxSummaryWrapper className='WarningBoxSummaryWrapper' fontSize={fontSize}>
                    <WarningBoxAlertWrapper fontSize={fontSize} color={borderColor}>
                        <Alert type={summary.type as AlertProps['type']} message={summary.message} description={summary.description} showIcon />
                    </WarningBoxAlertWrapper>
                </WarningBoxSummaryWrapper>
            )}
            <Col className='WarningCardsItem' span={24} style={{ height: `calc(100% - ${summaryHeight}px)` }}>
                {rows.map((warningGroup, rowIndex) => (
                    <Row key={`DataRow-${rowIndex}`} style={{ height: '100%' }}>
                        {warningGroup.map((warningCard) => (
                            <WarningCardContainer
                                key={warningCard.id}
                                className={blockBackgroundColor ? 'AlertColourBackground' : 'NoAlertColourBackground'}
                                fontSize={fontSize}
                                span={24 / rowSize}
                            >
                                <WarningCard card={warningCard} fontSize={fontSize} blockBackgroundColor={blockBackgroundColor} />
                            </WarningCardContainer>
                        ))}
                    </Row>
                ))}
            </Col>
        </Container>
    );
};

export default WarningBox;
